import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Lock, LogOut, Loader2, Building2, Plus, Trash2, ChevronDown, ChevronRight,
  RefreshCw, Save, X, Pencil, FileBadge,
} from "lucide-react";
import Logo from "../components/Logo";
import { ENTITY_CATEGORIES } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "isec_admin_token";
const CATEGORIES = ENTITY_CATEGORIES.filter((c) => c !== "All Categories");
const STATUSES = ["Active", "Suspended", "Revoked"];

const statusClasses = {
  Active: "bg-[#eaf4ee] text-[#137a47]",
  Suspended: "bg-amber-50 text-amber-700",
  Revoked: "bg-red-50 text-red-700",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const [expanded, setExpanded] = useState({});
  const [editingCompany, setEditingCompany] = useState(null);
  const [editName, setEditName] = useState("");
  const [licenseDraft, setLicenseDraft] = useState({});

  useEffect(() => { document.title = "Admin | ISEC Sierra Leone"; }, []);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const handleAuthError = (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setError("Session expired. Please log in again.");
    } else {
      setError("Something went wrong. Please try again.");
    }
  };

  const loadCompanies = async (t = token) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/companies`, { headers: { Authorization: `Bearer ${t}` } });
      setCompanies(res.data);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadCompanies(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, creds);
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setError("Invalid username or password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setCompanies([]);
    setCreds({ username: "", password: "" });
  };

  const addCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.trim()) return;
    try {
      await axios.post(`${API}/admin/companies`, { name: newCompany.trim() }, authHeaders);
      setNewCompany("");
      loadCompanies();
    } catch (err) { handleAuthError(err); }
  };

  const saveCompanyName = async (id) => {
    if (!editName.trim()) return;
    try {
      await axios.put(`${API}/admin/companies/${id}`, { name: editName.trim() }, authHeaders);
      setEditingCompany(null);
      loadCompanies();
    } catch (err) { handleAuthError(err); }
  };

  const deleteCompany = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its licences? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/companies/${id}`, authHeaders);
      loadCompanies();
    } catch (err) { handleAuthError(err); }
  };

  const draftFor = (cid) => licenseDraft[cid] || { license_number: "", category: CATEGORIES[0], status: "Active", issue_date: "" };
  const setDraft = (cid, patch) => setLicenseDraft((d) => ({ ...d, [cid]: { ...draftFor(cid), ...patch } }));

  const addLicense = async (cid) => {
    const d = draftFor(cid);
    if (!d.license_number.trim()) { setError("Licence number is required."); return; }
    try {
      await axios.post(`${API}/admin/companies/${cid}/licenses`, d, authHeaders);
      setLicenseDraft((s) => ({ ...s, [cid]: undefined }));
      loadCompanies();
    } catch (err) { handleAuthError(err); }
  };

  const updateLicense = async (id, patch) => {
    try {
      await axios.put(`${API}/admin/licenses/${id}`, patch, authHeaders);
      loadCompanies();
    } catch (err) { handleAuthError(err); }
  };

  const deleteLicense = async (id) => {
    if (!window.confirm("Delete this licence?")) return;
    try {
      await axios.delete(`${API}/admin/licenses/${id}`, authHeaders);
      loadCompanies();
    } catch (err) { handleAuthError(err); }
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const totalLicenses = companies.reduce((n, c) => n + (c.licenses?.length || 0), 0);

  // ---------------- Login ----------------
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-16">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <Logo size={56} />
            <h1 className="font-serif text-2xl font-bold text-brand-navy mt-4">Admin Sign In</h1>
            <p className="text-sm text-slate-500 mt-1">ISEC Sierra Leone — Register Management</p>
          </div>
          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">Username</label>
              <input value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm" placeholder="Username" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">Password</label>
              <input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm" placeholder="Password" />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-semibold px-6 py-3.5 rounded-md hover:bg-[#0a2444] transition-colors disabled:opacity-70">
              {loading ? <Loader2 size={17} className="animate-spin" /> : <Lock size={17} />}
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---------------- Dashboard ----------------
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-brand-navy text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={40} light />
            <div>
              <h1 className="font-serif text-lg font-bold leading-tight">ISEC Admin</h1>
              <p className="text-xs text-slate-300">Regulated entities &amp; licences</p>
            </div>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold bg-white/10 border border-white/30 px-4 py-2 rounded-md hover:bg-white/20 transition-colors">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Add company */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <h2 className="font-serif text-xl font-bold text-brand-navy mb-4 flex items-center gap-2">
            <Building2 size={20} /> Add a company
          </h2>
          <form onSubmit={addCompany} className="flex flex-col sm:flex-row gap-3">
            <input value={newCompany} onChange={(e) => setNewCompany(e.target.value)} placeholder="Company / entity name"
              className="flex-1 px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm" />
            <button type="submit" className="inline-flex items-center justify-center gap-2 bg-brand-green text-white font-semibold px-6 py-3 rounded-md hover:bg-[#0f6339] transition-colors">
              <Plus size={17} /> Add company
            </button>
          </form>
        </div>

        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl font-bold text-brand-navy">
            Companies <span className="text-slate-400 text-base">({companies.length})</span>
            <span className="text-slate-400 text-sm font-normal ml-2">· {totalLicenses} licences</span>
          </h2>
          <button onClick={() => loadCompanies()} disabled={loading} className="inline-flex items-center gap-2 text-sm font-semibold text-brand-green hover:underline">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

        {companies.length === 0 && !loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
            <Building2 size={40} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-sm">No companies yet. Add your first company above.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {companies.map((c) => {
              const d = draftFor(c.id);
              const isOpen = !!expanded[c.id];
              return (
                <div key={c.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  {/* Company header */}
                  <div className="flex items-center gap-3 px-5 py-4">
                    <button onClick={() => toggle(c.id)} className="text-slate-400 hover:text-brand-navy">
                      {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </button>
                    {editingCompany === c.id ? (
                      <div className="flex-1 flex items-center gap-2">
                        <input value={editName} onChange={(e) => setEditName(e.target.value)}
                          className="flex-1 px-3 py-2 rounded-md border border-slate-300 focus:border-brand-green outline-none text-sm" />
                        <button onClick={() => saveCompanyName(c.id)} className="text-brand-green p-1.5" title="Save"><Save size={18} /></button>
                        <button onClick={() => setEditingCompany(null)} className="text-slate-400 p-1.5" title="Cancel"><X size={18} /></button>
                      </div>
                    ) : (
                      <>
                        <button onClick={() => toggle(c.id)} className="flex-1 text-left">
                          <span className="font-semibold text-brand-navy">{c.name}</span>
                          <span className="text-xs text-slate-400 ml-2">{c.licenses?.length || 0} licence(s)</span>
                        </button>
                        <button onClick={() => { setEditingCompany(c.id); setEditName(c.name); }} className="text-slate-400 hover:text-brand-navy p-1.5" title="Rename"><Pencil size={16} /></button>
                        <button onClick={() => deleteCompany(c.id, c.name)} className="text-slate-400 hover:text-red-600 p-1.5" title="Delete company"><Trash2 size={16} /></button>
                      </>
                    )}
                  </div>

                  {/* Licences */}
                  {isOpen && (
                    <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-4">
                      {(c.licenses || []).length === 0 ? (
                        <p className="text-sm text-slate-400 mb-4">No licences yet for this company.</p>
                      ) : (
                        <div className="space-y-2 mb-4">
                          {c.licenses.map((l) => (
                            <div key={l.id} className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2.5">
                              <FileBadge size={16} className="text-brand-navy shrink-0" />
                              <span className="font-mono text-sm text-slate-700 min-w-[130px]">{l.license_number}</span>
                              <span className="text-sm text-slate-500 flex-1 min-w-[140px]">{l.category}</span>
                              <select value={l.status} onChange={(e) => updateLicense(l.id, { status: e.target.value })}
                                className={`text-xs font-semibold px-2.5 py-1.5 rounded-full border-0 outline-none cursor-pointer ${statusClasses[l.status]}`}>
                                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                              <button onClick={() => deleteLicense(l.id)} className="text-slate-400 hover:text-red-600 p-1" title="Delete licence"><Trash2 size={15} /></button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add licence */}
                      <div className="bg-white rounded-lg border border-dashed border-slate-300 p-3">
                        <p className="text-xs font-semibold text-slate-500 mb-2">Add a licence</p>
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                          <input value={d.license_number} onChange={(e) => setDraft(c.id, { license_number: e.target.value })} placeholder="Licence no. e.g. ISEC/BD/0110"
                            className="sm:col-span-4 px-3 py-2 rounded-md border border-slate-300 focus:border-brand-green outline-none text-sm" />
                          <select value={d.category} onChange={(e) => setDraft(c.id, { category: e.target.value })}
                            className="sm:col-span-4 px-3 py-2 rounded-md border border-slate-300 focus:border-brand-green outline-none text-sm bg-white">
                            {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
                          </select>
                          <select value={d.status} onChange={(e) => setDraft(c.id, { status: e.target.value })}
                            className="sm:col-span-2 px-3 py-2 rounded-md border border-slate-300 focus:border-brand-green outline-none text-sm bg-white">
                            {STATUSES.map((s) => <option key={s}>{s}</option>)}
                          </select>
                          <button onClick={() => addLicense(c.id)}
                            className="sm:col-span-2 inline-flex items-center justify-center gap-1.5 bg-brand-navy text-white text-sm font-semibold px-3 py-2 rounded-md hover:bg-[#0a2444] transition-colors">
                            <Plus size={15} /> Add
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
